import cn from "classnames";
import type { PolicyReference } from "../../db/schema";
import { findPolicyReference, type SuggestionTag } from "./suggestionTags";

interface SuggestionTagBadgesProps {
  tag: SuggestionTag;
  className?: string;
  /** Makes policy badges clickable (the sidebar opens the section) */
  onOpenPolicy?: (label: string, reference?: PolicyReference) => void;
  policyReferences?: PolicyReference[];
}

// The badges under a policy- or footage-check suggestion (see
// suggestionTags.ts). Shared by the Suggestions sidebar and the Add to Report
// modals' "points to cover" lists, which show them read-only.
function SuggestionTagBadges({ tag, className, onOpenPolicy, policyReferences }: SuggestionTagBadgesProps) {
  if (tag.kind === "policy") {
    const badge =
      "inline-block max-w-full rounded-md px-2 py-0.5 text-left text-[11px] font-medium leading-4 whitespace-normal break-words bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    return (
      <div className={cn("flex flex-wrap gap-1", className)}>
        {tag.parts.map((part, i) =>
          onOpenPolicy ? (
            <button
              key={i}
              type="button"
              data-tour="policy-badge"
              className={cn(badge, "hover:bg-blue-100 dark:hover:bg-blue-900/70 hover:underline")}
              title="Open this section of the policy"
              onClick={(e) => {
                // Inside the row's <label>: don't toggle the checkbox.
                e.preventDefault();
                e.stopPropagation();
                onOpenPolicy(part, findPolicyReference(part, policyReferences));
              }}
            >
              {part}
            </button>
          ) : (
            <span key={i} className={badge} title={part}>
              {part}
            </span>
          ),
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {tag.parts.map((part, i) => (
        <span
          key={i}
          className={cn(
            "inline-block max-w-full px-2 py-0.5 text-[11px] font-medium leading-4",
            // The kind is a pill; the reference may wrap onto a second line.
            i === 0
              ? "rounded-full whitespace-nowrap bg-violet-500 text-white"
              : "rounded-md whitespace-normal break-words text-left bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
          )}
          title={part}
        >
          {part}
        </span>
      ))}
    </div>
  );
}

export default SuggestionTagBadges;
